import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaPlus, FaFileInvoiceDollar, FaFilter, FaFilePdf, FaTrash } from 'react-icons/fa';
import Swal from 'sweetalert2';
import '../../styles/pages/RRHH/LiquidacionesPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const MESES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

// Estado inicial del formulario. Solo datos que el usuario elige o digita:
// las cotizaciones y el impuesto los calcula el backend.
const FORM_VACIO = {
    id_colaborador: '',
    mes: '',
    anio: '',
    dias_trabajados: 30,
    sueldo_base: '',
    horas_extras: 0,
    bono_produccion: 0,
    aguinaldo: 0,
    otros_imponibles: 0,
    colacion: 0,
    movilizacion: 0,
    otros_haberes: 0,
    anticipo: 0
};

/**
 * Página de Liquidaciones de Sueldo.
 *
 * El formulario solo pide datos. Todo el cálculo (gratificación, AFP, AFC, salud e
 * impuesto único) vive en el backend, en liquidacionesService, que es el mismo que
 * usa la generación por período. El usuario genera, ve el PDF y, si no le cuadra,
 * elimina y crea de nuevo.
 */
const LiquidacionesPage = ({ token, id_company }) => {
    const [liquidaciones, setLiquidaciones] = useState([]);
    const [colaboradores, setColaboradores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [sueldoBaseMensual, setSueldoBaseMensual] = useState(0);

    const getPreviousMonth = () => {
        const date = new Date();
        const prevMonth = date.getMonth();
        const year = prevMonth === 0 ? date.getFullYear() - 1 : date.getFullYear();
        const month = prevMonth === 0 ? 12 : prevMonth;
        return { month, year };
    };

    const previousMonthData = getPreviousMonth();

    const [filtroMes, setFiltroMes] = useState(previousMonthData.month);
    const [filtroAnio, setFiltroAnio] = useState(previousMonthData.year);

    const [formData, setFormData] = useState(FORM_VACIO);

    useEffect(() => {
        fetchColaboradores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchLiquidaciones();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtroMes, filtroAnio]);

    // Colaboradores activos que todavía no tienen finiquito pagado: son los únicos
    // a los que corresponde generarles una liquidación.
    const fetchColaboradores = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/rrhh/colaboradores`, {
                params: { id_company, estado: 'ACTIVO', excluir_finiquitados: 'true' },
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setColaboradores(response.data.data);
            }
        } catch (error) {
            console.error('Error al cargar colaboradores:', error);
        }
    };

    const fetchLiquidaciones = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE_URL}/api/rrhh/liquidaciones`, {
                params: { id_company, mes: filtroMes, anio: filtroAnio },
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setLiquidaciones(response.data.data);
            }
        } catch (error) {
            console.error('Error al cargar liquidaciones:', error);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Comprueba que existan indicadores previsionales del período. No los guarda: acá
     * solo sirven para avisar temprano, el cálculo los busca de nuevo en el backend.
     */
    const verificarIndicadores = async (mes, anio) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/rrhh/indicadores-previred`, {
                params: { mes, anio },
                headers: { Authorization: `Bearer ${token}` }
            });
            return !!(response.data.success && response.data.data);
        } catch (error) {
            console.error('Error al consultar indicadores:', error);
            return false;
        }
    };

    const handleColaboradorChange = (e) => {
        const idColaborador = e.target.value;
        const selectedCol = colaboradores.find(c => c.id === parseInt(idColaborador));

        if (!selectedCol) {
            setSueldoBaseMensual(0);
            setFormData({ ...formData, id_colaborador: '', sueldo_base: '' });
            return;
        }

        // Sin AFP o sin sistema de salud en la ficha no hay con qué calcular las
        // cotizaciones, así que se rechaza acá en vez de fallar al generar.
        const camposFaltantes = [];
        if (!selectedCol.afp) camposFaltantes.push('AFP');
        if (!selectedCol.isapre_fonasa) camposFaltantes.push('Salud (Isapre/Fonasa)');

        if (camposFaltantes.length > 0) {
            Swal.fire({
                title: 'Datos Incompletos',
                html: `El colaborador <strong>${selectedCol.nombres} ${selectedCol.apellido_paterno}</strong> no tiene configurados los siguientes datos previsionales:<br><br>
                       <ul style="text-align: left; display: inline-block;">
                       ${camposFaltantes.map(c => `<li>${c}</li>`).join('')}
                       </ul><br>
                       Complete estos datos en la ficha del colaborador antes de generar una liquidación.`,
                icon: 'warning',
                confirmButtonText: 'Entendido'
            });
            setFormData({ ...formData, id_colaborador: '', sueldo_base: '' });
            return;
        }

        // El sueldo base se propone desde la ficha, prorrateado por los días trabajados.
        // Queda editable: es un dato que el usuario puede ajustar a mano.
        const sueldoCompleto = parseFloat(selectedCol.sueldo_base) || 0;
        setSueldoBaseMensual(sueldoCompleto);

        const dias = formData.dias_trabajados || 30;
        const sueldoAjustado = dias < 30 ? Math.round((sueldoCompleto / 30) * dias) : sueldoCompleto;

        setFormData({
            ...formData,
            id_colaborador: idColaborador,
            sueldo_base: sueldoAjustado || ''
        });
    };

    // Validación de entrada. Corre cuando ya están elegidos los tres campos que
    // identifican una liquidación —colaborador, mes y año—, sin importar el orden,
    // y vuelve a correr si se cambia cualquiera. Avisa antes de que el usuario
    // termine de llenar el formulario en vano; el backend valida igual al guardar.
    useEffect(() => {
        if (!formData.id_colaborador || !formData.mes || !formData.anio) return;

        let cancelado = false;

        (async () => {
            try {
                const { data } = await axios.get(`${API_BASE_URL}/api/rrhh/liquidaciones`, {
                    params: {
                        id_company,
                        mes: formData.mes,
                        anio: formData.anio,
                        id_colaborador: formData.id_colaborador
                    },
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (cancelado) return;

                if (data.success && data.data.length > 0) {
                    const col = colaboradores.find(c => c.id === parseInt(formData.id_colaborador));
                    const nombre = col ? `${col.nombres} ${col.apellido_paterno}` : 'El colaborador';
                    Swal.fire({
                        title: 'Ya existe una liquidación para este período',
                        html: `<strong>${nombre}</strong> ya tiene una liquidación de
                               <strong>${MESES[formData.mes - 1]} ${formData.anio}</strong>.<br><br>
                               Si necesita corregirla, elimine la existente desde la tabla y vuelva a crearla.`,
                        icon: 'error',
                        confirmButtonText: 'Entendido'
                    });
                    setFormData(prev => ({ ...prev, id_colaborador: '', sueldo_base: '' }));
                    return;
                }
            } catch (error) {
                console.error('Error al verificar liquidación existente:', error);
                // No se corta el flujo: el backend rechaza igual el duplicado con un 409.
            }

            const hayIndicadores = await verificarIndicadores(formData.mes, formData.anio);
            if (cancelado || hayIndicadores) return;

            Swal.fire({
                title: 'No se puede crear la liquidación',
                html: `No hay indicadores previsionales cargados para el período <strong>${formData.mes}/${formData.anio}</strong>.<br><br>
                       Sin ellos no se pueden calcular AFP, AFC, salud ni impuesto único.
                       Cárguelos en <strong>Indicadores Previsionales</strong> antes de generar liquidaciones de este período.`,
                icon: 'error',
                confirmButtonText: 'Entendido'
            });
            setFormData(prev => ({ ...prev, id_colaborador: '', sueldo_base: '' }));
        })();

        return () => { cancelado = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.id_colaborador, formData.mes, formData.anio]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setGuardando(true);

        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/rrhh/liquidaciones`,
                { ...formData, id_company },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                const pdfUrl = response.data.pdf_url;
                setShowModal(false);
                resetForm();
                fetchLiquidaciones();

                Swal.fire({
                    title: 'Liquidación generada',
                    text: 'Revise el PDF. Si algo no le cuadra, elimínela y créela de nuevo.',
                    icon: 'success',
                    showCancelButton: true,
                    confirmButtonText: 'Ver PDF',
                    cancelButtonText: 'Cerrar'
                }).then((result) => {
                    if (result.isConfirmed && pdfUrl) {
                        window.open(pdfUrl.startsWith('http') ? pdfUrl : `${API_BASE_URL}/uploads/${pdfUrl}`, '_blank');
                    }
                });
            }
        } catch (error) {
            console.error('Error al generar liquidación:', error);
            Swal.fire('No se generó la liquidación', error.response?.data?.message || 'Error al guardar', 'error');
        } finally {
            setGuardando(false);
        }
    };

    const handleDelete = async (liquidacion) => {
        const result = await Swal.fire({
            title: '¿Eliminar liquidación?',
            html: `¿Está seguro de eliminar la liquidación de <strong>${liquidacion.nombres} ${liquidacion.apellido_paterno}</strong> del período ${MESES[liquidacion.mes - 1]} ${liquidacion.anio}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                const response = await axios.delete(`${API_BASE_URL}/api/rrhh/liquidaciones/${liquidacion.id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.success) {
                    Swal.fire('Eliminada', response.data.message, 'success');
                    fetchLiquidaciones();
                }
            } catch (error) {
                console.error('Error al eliminar liquidación:', error);
                Swal.fire('Error', error.response?.data?.message || 'Error al eliminar la liquidación', 'error');
            }
        }
    };

    const resetForm = () => {
        setFormData(FORM_VACIO);
        setSueldoBaseMensual(0);
    };

    // Campo de monto: se digita en pesos y se muestra con separador de miles.
    const campoMonto = (label, campo) => (
        <div className="form-group" key={campo}>
            <label>{label}</label>
            <input
                type="text"
                inputMode="numeric"
                value={parseInt(formData[campo] || 0).toLocaleString('es-CL')}
                onChange={(e) => setFormData({ ...formData, [campo]: parseInt(e.target.value.replace(/\D/g, '') || 0) })}
            />
        </div>
    );

    return (
        <div className="liquidaciones-container">
            <div className="liquidaciones-header">
                <h1><FaFileInvoiceDollar /> Liquidaciones de Sueldo</h1>
                <button className="btn-nuevo" onClick={() => { resetForm(); setShowModal(true); }}>
                    <FaPlus /> Nueva Liquidación
                </button>
            </div>

            {/* Filtros */}
            <div className="liquidaciones-filters">
                <div className="filter-group">
                    <label><FaFilter /> Mes</label>
                    <select value={filtroMes} onChange={(e) => setFiltroMes(parseInt(e.target.value))}>
                        {MESES.map((mes, index) => (
                            <option key={index} value={index + 1}>{mes}</option>
                        ))}
                    </select>
                </div>
                <div className="filter-group">
                    <label>Año</label>
                    <select value={filtroAnio} onChange={(e) => setFiltroAnio(parseInt(e.target.value))}>
                        {[2024, 2025, 2026].map(year => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Tabla de liquidaciones */}
            {loading ? (
                <div className="loading">Cargando liquidaciones...</div>
            ) : (
                <div className="liquidaciones-table-container">
                    <table className="liquidaciones-table">
                        <thead>
                            <tr>
                                <th>Colaborador</th>
                                <th>Período</th>
                                <th>Total Haberes</th>
                                <th>Total Descuentos</th>
                                <th>Líquido a Pagar</th>
                                <th>Estado</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {liquidaciones.length === 0 ? (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: 'center', padding: '30px' }}>
                                        No hay liquidaciones para este período
                                    </td>
                                </tr>
                            ) : (
                                liquidaciones.map((liq) => (
                                    <tr key={liq.id}>
                                        <td>{liq.nombres} {liq.apellido_paterno}</td>
                                        <td>{MESES[liq.mes - 1]} {liq.anio}</td>
                                        <td className="amount">${parseInt(liq.total_haberes || 0).toLocaleString('es-CL')}</td>
                                        <td className="amount">${parseInt(liq.total_descuentos || 0).toLocaleString('es-CL')}</td>
                                        <td className="amount liquido">${parseInt(liq.liquido_pagar || 0).toLocaleString('es-CL')}</td>
                                        <td>
                                            <span className={`badge badge-${liq.estado === 'PAGADA' ? 'pagada' : 'propuesta'}`}>
                                                {liq.estado === 'PAGADA' ? 'Pagada' : 'Propuesta'}
                                            </span>
                                        </td>
                                        <td className="actions-cell">
                                            {/* Toda liquidación se crea con su PDF en la misma
                                                transacción, así que siempre hay uno que ver. */}
                                            <button
                                                className="btn-pdf"
                                                onClick={() => window.open(liq.pdf_url.startsWith('http') ? liq.pdf_url : `${API_BASE_URL}/uploads/${liq.pdf_url}`, '_blank')}
                                                title="Ver PDF"
                                            >
                                                <FaFilePdf />
                                            </button>
                                            <button className="btn-delete" onClick={() => handleDelete(liq)} title="Eliminar">
                                                <FaTrash />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal de formulario */}
            {showModal && (
                <div className="modal-overlay" onClick={() => { setShowModal(false); resetForm(); }}>
                    <div className="modal-content liquidaciones-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Nueva Liquidación</h2>
                        <p className="modal-nota">
                            La gratificación, AFP, AFC, salud e impuesto único los calcula el sistema con los
                            indicadores del período. Aquí solo se ingresan los datos del mes.
                        </p>
                        <form onSubmit={handleSubmit}>
                            <div className="form-section">
                                <h3>Información General</h3>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Colaborador *</label>
                                        <select
                                            value={formData.id_colaborador}
                                            onChange={handleColaboradorChange}
                                            required
                                        >
                                            <option value="">Seleccione...</option>
                                            {colaboradores.map(col => (
                                                <option key={col.id} value={col.id}>
                                                    {col.nombres} {col.apellido_paterno}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Mes *</label>
                                        <select
                                            value={formData.mes}
                                            onChange={(e) => setFormData({ ...formData, mes: e.target.value ? parseInt(e.target.value) : '' })}
                                            required
                                        >
                                            <option value="">Seleccione...</option>
                                            {MESES.map((mes, index) => (
                                                <option key={index} value={index + 1}>{mes}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Año *</label>
                                        <select
                                            value={formData.anio}
                                            onChange={(e) => setFormData({ ...formData, anio: e.target.value ? parseInt(e.target.value) : '' })}
                                            required
                                        >
                                            <option value="">Seleccione...</option>
                                            {[2024, 2025, 2026].map(year => (
                                                <option key={year} value={year}>{year}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Días Trabajados</label>
                                        <input
                                            type="number"
                                            min="1"
                                            max="30"
                                            value={formData.dias_trabajados}
                                            onChange={(e) => {
                                                const dias = parseInt(e.target.value) || 0;
                                                if (sueldoBaseMensual > 0) {
                                                    const sueldoAjustado = dias < 30 ? Math.round((sueldoBaseMensual / 30) * dias) : sueldoBaseMensual;
                                                    setFormData({ ...formData, dias_trabajados: dias, sueldo_base: sueldoAjustado });
                                                } else {
                                                    setFormData({ ...formData, dias_trabajados: dias });
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-section">
                                <h3>Haberes imponibles</h3>
                                <div className="form-grid">
                                    {campoMonto('Sueldo Base *', 'sueldo_base')}
                                    {campoMonto('Horas Extras', 'horas_extras')}
                                    {campoMonto('Bono Producción', 'bono_produccion')}
                                    {campoMonto('Aguinaldo', 'aguinaldo')}
                                    {campoMonto('Otros Imponibles', 'otros_imponibles')}
                                </div>
                            </div>

                            <div className="form-section">
                                <h3>Haberes no imponibles</h3>
                                <div className="form-grid">
                                    {campoMonto('Colación', 'colacion')}
                                    {campoMonto('Movilización', 'movilizacion')}
                                    {campoMonto('Otros Haberes', 'otros_haberes')}
                                </div>
                            </div>

                            <div className="form-section">
                                <h3>Descuentos voluntarios</h3>
                                <div className="form-grid">
                                    {campoMonto('Anticipo', 'anticipo')}
                                </div>
                            </div>

                            <div className="modal-actions">
                                <button type="button" className="btn-cancel" onClick={() => { setShowModal(false); resetForm(); }}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-save" disabled={guardando}>
                                    {guardando ? 'Generando...' : 'Generar Liquidación'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LiquidacionesPage;
